import React, { useRef, useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Editor } from "@tiptap/react";
import { FiUpload, FiDownload, FiSave } from "react-icons/fi";
import {
  BsArrowCounterclockwise,
  BsArrowClockwise,
  BsScissors,
  BsClipboard,
  BsFiles,
  BsTextParagraph,
  BsCardChecklist,
  BsChatDots,
  BsClockHistory,
  BsFileDiff,
  BsCheck,
  BsToggleOn,
  BsToggleOff,
  BsImage,
  BsTable,
  BsTypeBold,
  BsTypeItalic,
  BsTypeUnderline,
  BsTypeStrikethrough,
  BsSubscript,
  BsSuperscript,
  BsTextLeft,
  BsTextCenter,
  BsTextRight,
  BsJustify,
  BsListUl,
  BsListOl,
  BsEraser,
  BsSearch,
  BsLink45Deg,
  BsListNested,
  BsTextIndentLeft,
  BsTextIndentRight,
  BsDistributeVertical,
  BsPrinter,
  BsKeyboard,
  BsFilePdf,
  BsLayoutTextWindowReverse,
  BsJournalText,
  BsJournals,
  BsAspectRatio,
  BsLayoutThreeColumns,
  BsSquare,
} from "react-icons/bs";
import { VscHorizontalRule } from "react-icons/vsc";
import { TbMathFunction } from "react-icons/tb";
import { LuHeading1, LuHeading2, LuHeading3 } from "react-icons/lu";
import { BiStats } from "react-icons/bi";
import { MdHorizontalRule } from "react-icons/md";
import "./EditorMenuBar.css";

// ============================================================================
// TYPES
// ============================================================================

interface EditorMenuBarProps {
  // Editor instance for edit operations
  editor?: Editor | null;
  // File menu actions
  onNewDocument?: () => void;
  onSave?: () => void;
  isSaving?: boolean;
  onImportDocx?: (file: File) => void;
  onImportPdf?: (file: File) => void;
  onExportDocx?: () => void;
  onExportPdf?: () => void;
  onPrint?: () => void;
  onPageSetup?: () => void;
  // View menu - Track Changes
  trackChangesEnabled?: boolean;
  onToggleTrackChanges?: () => void;
  showTrackChangesPanel?: boolean;
  onToggleTrackChangesPanel?: () => void;
  trackChangesCount?: number;
  // View menu - Comments
  showCommentsPanel?: boolean;
  onToggleCommentsPanel?: () => void;
  commentsCount?: number;
  // View menu - Outline
  showOutlinePanel?: boolean;
  onToggleOutlinePanel?: () => void;
  // Edit menu - Find & Replace
  onFindReplace?: () => void;
  // Insert menu
  onAddComment?: () => void;
  onInsertMath?: () => void;
  onInsertSpecialChar?: () => void;
  onInsertLink?: () => void;
  onEditHeaderFooter?: () => void;
  onWatermark?: () => void;
  // Tools menu
  onShowTextStats?: () => void;
  // Version history
  onVersionHistory?: () => void;
  // Help menu
  onShowKeyboardShortcuts?: () => void;
  // Read-only mode
  readOnly?: boolean;
  // Image upload — when provided, images inserted via menu bar are uploaded to
  // GCS in the background after an instant base64 preview.
  onImageUpload?: (file: File) => Promise<{ src: string; gcsFileName: string }>;
}

interface MenuItemProps {
  icon?: React.ReactNode;
  label: string;
  shortcut?: string;
  onClick?: () => void;
  disabled?: boolean;
  dividerAfter?: boolean;
  checked?: boolean;
  badge?: number;
}

// ============================================================================
// MENU ITEM COMPONENT
// ============================================================================

const MenuItem: React.FC<MenuItemProps> = ({
  icon,
  label,
  shortcut,
  onClick,
  disabled = false,
  dividerAfter = false,
  checked,
  badge,
}) => {
  return (
    <>
      <button
        type="button"
        className={`menu-item ${disabled ? "disabled" : ""} ${checked ? "checked" : ""}`}
        onClick={onClick}
        disabled={disabled}
      >
        <span className="menu-item-check">
          {checked !== undefined && (checked ? <BsCheck /> : null)}
        </span>
        <span className="menu-item-icon">{icon}</span>
        <span className="menu-item-label">{label}</span>
        {badge !== undefined && badge > 0 && (
          <span className="menu-item-badge">{badge}</span>
        )}
        {shortcut && <span className="menu-item-shortcut">{shortcut}</span>}
      </button>
      {dividerAfter && <div className="menu-divider" />}
    </>
  );
};

// Platform-aware modifier label for displayed shortcuts (binding is Mod-*,
// which TipTap maps to ⌘ on macOS and Ctrl elsewhere).
const IS_MAC =
  typeof navigator !== "undefined" &&
  /Mac|iPod|iPhone|iPad/.test(navigator.platform);
const MOD_LABEL = IS_MAC ? "⌘" : "Ctrl";

// ============================================================================
// LINE HEIGHT OPTIONS
// ============================================================================

const LINE_HEIGHT_OPTIONS = [
  { label: "Single", value: "1" },
  { label: "1.15", value: "1.15" },
  { label: "1.5", value: "1.5" },
  { label: "Double", value: "2" },
  { label: "2.5", value: "2.5" },
  { label: "3", value: "3" },
];

// ============================================================================
// EDITOR MENU BAR COMPONENT
// ============================================================================

const EditorMenuBar: React.FC<EditorMenuBarProps> = ({
  editor,
  onNewDocument,
  onSave,
  isSaving,
  onImportDocx,
  onImportPdf,
  onExportDocx,
  onExportPdf,
  onPrint,
  onPageSetup,
  // View menu - Track Changes
  trackChangesEnabled,
  onToggleTrackChanges,
  showTrackChangesPanel,
  onToggleTrackChangesPanel,
  trackChangesCount = 0,
  // View menu - Comments
  showCommentsPanel,
  onToggleCommentsPanel,
  commentsCount = 0,
  // View menu - Outline
  showOutlinePanel,
  onToggleOutlinePanel,
  // Edit menu - Find & Replace
  onFindReplace,
  // Insert menu
  onAddComment,
  onInsertMath,
  onInsertSpecialChar,
  onInsertLink,
  onEditHeaderFooter,
  // Tools menu
  onWatermark,
  onShowTextStats,
  // Version history
  onVersionHistory,
  // Help menu
  onShowKeyboardShortcuts,
  readOnly,
  onImageUpload,
}) => {
  const { t } = useTranslation();
  const [activeMenu, setActiveMenu] = useState<string | null>(null);
  const [showTablePicker, setShowTablePicker] = useState(false);
  const [showCustomTable, setShowCustomTable] = useState(false);
  const [customRows, setCustomRows] = useState(3);
  const [customCols, setCustomCols] = useState(3);
  const menuBarRef = useRef<HTMLDivElement>(null);
  const docxInputRef = useRef<HTMLInputElement>(null);
  const pdfInputRef = useRef<HTMLInputElement>(null);
  const imageInputRef = useRef<HTMLInputElement>(null);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        menuBarRef.current &&
        !menuBarRef.current.contains(event.target as Node)
      ) {
        setActiveMenu(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setActiveMenu(null);
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  const toggleMenu = (menuName: string) => {
    setActiveMenu(prev => (prev === menuName ? null : menuName));
  };

  const closeMenu = () => {
    setActiveMenu(null);
  };

  // File menu handlers
  const handleNewDocument = useCallback(() => {
    if (onNewDocument) {
      onNewDocument();
      closeMenu();
    }
  }, [onNewDocument]);

  const handleSave = useCallback(() => {
    if (onSave) {
      onSave();
      closeMenu();
    }
  }, [onSave]);

  const handleVersionHistory = useCallback(() => {
    if (onVersionHistory) {
      onVersionHistory();
      closeMenu();
    }
  }, [onVersionHistory]);

  const handleImportDocxClick = useCallback(() => {
    docxInputRef.current?.click();
    closeMenu();
  }, []);

  const handleImportDocx = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (file && onImportDocx) {
        onImportDocx(file);
      }
      // Reset input
      if (docxInputRef.current) {
        docxInputRef.current.value = "";
      }
    },
    [onImportDocx]
  );

  const handleImportPdfClick = useCallback(() => {
    pdfInputRef.current?.click();
    closeMenu();
  }, []);

  const handleImportPdf = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (file && onImportPdf) {
        onImportPdf(file);
      }
      // Reset input
      if (pdfInputRef.current) {
        pdfInputRef.current.value = "";
      }
    },
    [onImportPdf]
  );

  const handleExport = useCallback(() => {
    if (onExportDocx) {
      onExportDocx();
      closeMenu();
    }
  }, [onExportDocx]);

  const handleExportPdf = useCallback(() => {
    if (onExportPdf) {
      onExportPdf();
      closeMenu();
    }
  }, [onExportPdf]);

  const handlePrint = useCallback(() => {
    if (onPrint) {
      onPrint();
      closeMenu();
    }
  }, [onPrint]);

  // ============================================================================
  // EDIT MENU HANDLERS
  // ============================================================================

  const handleUndo = useCallback(() => {
    if (editor) {
      editor.chain().focus().undo().run();
      closeMenu();
    }
  }, [editor]);

  const handleRedo = useCallback(() => {
    if (editor) {
      editor.chain().focus().redo().run();
      closeMenu();
    }
  }, [editor]);

  const handleCut = useCallback(() => {
    if (editor) {
      editor.commands.focus();
      document.execCommand("cut");
      closeMenu();
    }
  }, [editor]);

  const handleCopy = useCallback(() => {
    if (editor) {
      editor.commands.focus();
      document.execCommand("copy");
      closeMenu();
    }
  }, [editor]);

  const handlePaste = useCallback(() => {
    if (editor) {
      editor.commands.focus();
      document.execCommand("paste");
      closeMenu();
    }
  }, [editor]);

  const handlePasteWithoutFormatting = useCallback(async () => {
    if (editor) {
      try {
        const text = await navigator.clipboard.readText();
        if (text) {
          editor
            .chain()
            .focus()
            .insertContent(text, {
              parseOptions: { preserveWhitespace: "full" },
            })
            .run();
        }
      } catch (err) {
        editor.commands.focus();
      }
      closeMenu();
    }
  }, [editor]);

  const handleSelectAll = useCallback(() => {
    if (editor) {
      editor.chain().focus().selectAll().run();
      closeMenu();
    }
  }, [editor]);

  const handleFindReplace = useCallback(() => {
    if (onFindReplace) {
      onFindReplace();
      closeMenu();
    }
  }, [onFindReplace]);

  // ============================================================================
  // VIEW MENU HANDLERS
  // ============================================================================

  const handleToggleTrackChanges = useCallback(() => {
    if (onToggleTrackChanges) {
      onToggleTrackChanges();
      closeMenu();
    }
  }, [onToggleTrackChanges]);

  const handleToggleTrackChangesPanel = useCallback(() => {
    if (onToggleTrackChangesPanel) {
      onToggleTrackChangesPanel();
      closeMenu();
    }
  }, [onToggleTrackChangesPanel]);

  const handleToggleCommentsPanel = useCallback(() => {
    if (onToggleCommentsPanel) {
      onToggleCommentsPanel();
      closeMenu();
    }
  }, [onToggleCommentsPanel]);

  const handleToggleOutlinePanel = useCallback(() => {
    if (onToggleOutlinePanel) {
      onToggleOutlinePanel();
      closeMenu();
    }
  }, [onToggleOutlinePanel]);

  // ============================================================================
  // INSERT MENU HANDLERS
  // ============================================================================

  const handleInsertImageClick = useCallback(() => {
    imageInputRef.current?.click();
    closeMenu();
  }, []);

  const handleImageUpload = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (file && editor) {
        const reader = new FileReader();
        reader.onload = async e => {
          const base64 = e.target?.result;
          if (typeof base64 !== "string") return;
          // 1. Instant preview with base64
          editor.chain().focus().setImage({ src: base64 }).run();
          // 2. Background upload → swap src + set gcsFileName
          if (onImageUpload) {
            try {
              const { src: proxyUrl, gcsFileName } = await onImageUpload(file);
              // Use TipTap's chain API to build a single transaction
              editor
                .chain()
                .command(({ tr }) => {
                  let found = false;
                  tr.doc.descendants((node, pos) => {
                    if (
                      !found &&
                      node.type.name === "image" &&
                      node.attrs.src === base64
                    ) {
                      tr.setNodeMarkup(pos, undefined, {
                        ...node.attrs,
                        src: proxyUrl,
                        gcsFileName,
                      });
                      found = true;
                    }
                  });
                  return found;
                })
                .run();
            } catch (err) {
              console.error("[EditorMenuBar] Image upload failed:", err);
            }
          }
        };
        reader.readAsDataURL(file);
      }
      if (imageInputRef.current) {
        imageInputRef.current.value = "";
      }
    },
    [editor, onImageUpload]
  );

  const handleInsertTable = useCallback(
    (rows: number, cols: number) => {
      if (editor) {
        editor
          .chain()
          .focus()
          .insertTable({ rows, cols, withHeaderRow: true })
          .run();
        setShowTablePicker(false);
        setShowCustomTable(false);
        closeMenu();
      }
    },
    [editor]
  );

  const handleCustomTableSubmit = useCallback(() => {
    if (customRows > 0 && customCols > 0) {
      handleInsertTable(customRows, customCols);
    }
  }, [customRows, customCols, handleInsertTable]);

  const handleInsertHorizontalRule = useCallback(() => {
    if (editor) {
      editor.chain().focus().setHorizontalRule().run();
      closeMenu();
    }
  }, [editor]);

  const handleInsertPageBreak = useCallback(() => {
    if (editor) {
      editor.chain().focus().setPageBreak().run();
      closeMenu();
    }
  }, [editor]);

  const handleInsertMath = useCallback(() => {
    if (onInsertMath) {
      onInsertMath();
      closeMenu();
    }
  }, [onInsertMath]);

  const handleInsertSpecialChar = useCallback(() => {
    if (onInsertSpecialChar) {
      onInsertSpecialChar();
      closeMenu();
    }
  }, [onInsertSpecialChar]);

  const handleInsertComment = useCallback(() => {
    if (onAddComment) {
      onAddComment();
      closeMenu();
    }
  }, [onAddComment]);

  const handleInsertLink = useCallback(() => {
    if (onInsertLink) {
      onInsertLink();
      closeMenu();
    }
  }, [onInsertLink]);

  // ============================================================================
  // FORMAT MENU HANDLERS
  // ============================================================================

  const handleBold = useCallback(() => {
    editor?.chain().focus().toggleBold().run();
    closeMenu();
  }, [editor]);

  const handleItalic = useCallback(() => {
    editor?.chain().focus().toggleItalic().run();
    closeMenu();
  }, [editor]);

  const handleUnderline = useCallback(() => {
    editor?.chain().focus().toggleUnderline().run();
    closeMenu();
  }, [editor]);

  const handleStrikethrough = useCallback(() => {
    editor?.chain().focus().toggleStrike().run();
    closeMenu();
  }, [editor]);

  const handleSuperscript = useCallback(() => {
    editor?.chain().focus().toggleSuperscript().run();
    closeMenu();
  }, [editor]);

  const handleSubscript = useCallback(() => {
    editor?.chain().focus().toggleSubscript().run();
    closeMenu();
  }, [editor]);

  const handleHeading1 = useCallback(() => {
    editor?.chain().focus().toggleHeading({ level: 1 }).run();
    closeMenu();
  }, [editor]);

  const handleHeading2 = useCallback(() => {
    editor?.chain().focus().toggleHeading({ level: 2 }).run();
    closeMenu();
  }, [editor]);

  const handleHeading3 = useCallback(() => {
    editor?.chain().focus().toggleHeading({ level: 3 }).run();
    closeMenu();
  }, [editor]);

  const handleAlignLeft = useCallback(() => {
    editor?.chain().focus().setTextAlign("left").run();
    closeMenu();
  }, [editor]);

  const handleAlignCenter = useCallback(() => {
    editor?.chain().focus().setTextAlign("center").run();
    closeMenu();
  }, [editor]);

  const handleAlignRight = useCallback(() => {
    editor?.chain().focus().setTextAlign("right").run();
    closeMenu();
  }, [editor]);

  const handleAlignJustify = useCallback(() => {
    editor?.chain().focus().setTextAlign("justify").run();
    closeMenu();
  }, [editor]);

  const handleBulletList = useCallback(() => {
    editor?.chain().focus().toggleBulletList().run();
    closeMenu();
  }, [editor]);

  const handleOrderedList = useCallback(() => {
    editor?.chain().focus().toggleOrderedList().run();
    closeMenu();
  }, [editor]);

  const handleClearFormatting = useCallback(() => {
    editor?.chain().focus().unsetAllMarks().clearNodes().run();
    closeMenu();
  }, [editor]);

  const handleIndent = useCallback(() => {
    editor?.chain().focus().indent().run();
    closeMenu();
  }, [editor]);

  const handleOutdent = useCallback(() => {
    editor?.chain().focus().outdent().run();
    closeMenu();
  }, [editor]);

  const handleSetLineHeight = useCallback(
    (value: string) => {
      editor?.chain().focus().setLineHeight(value).run();
      closeMenu();
    },
    [editor]
  );

  // ============================================================================
  // TOOLS MENU HANDLERS
  // ============================================================================

  const handleShowTextStats = useCallback(() => {
    if (onShowTextStats) {
      onShowTextStats();
      closeMenu();
    }
  }, [onShowTextStats]);

  // ============================================================================
  // HELP MENU HANDLERS
  // ============================================================================

  const handleShowKeyboardShortcuts = useCallback(() => {
    if (onShowKeyboardShortcuts) {
      onShowKeyboardShortcuts();
      closeMenu();
    }
  }, [onShowKeyboardShortcuts]);

  // Get current line height for checkmark display
  const getCurrentLineHeight = (): string | null => {
    if (!editor) return null;
    const { $from } = editor.state.selection;
    const node = $from.parent;
    return node?.attrs?.lineHeight || null;
  };

  const currentLineHeight = getCurrentLineHeight();

  // Determine if File menu has any items
  const hasFileMenuItems =
    onNewDocument ||
    onSave ||
    onVersionHistory ||
    onImportDocx ||
    onImportPdf ||
    onExportDocx ||
    onExportPdf ||
    onPrint;

  return (
    <div className="editor-menu-bar" ref={menuBarRef}>
      {/* File Menu */}
      {hasFileMenuItems && (
        <div className="menu-dropdown">
          <button
            type="button"
            className={`menu-trigger ${activeMenu === "file" ? "active" : ""}`}
            onClick={() => toggleMenu("file")}
          >
            {t("tiptapEditor.menu.file")}
          </button>
          {activeMenu === "file" && (
            <div className="menu-dropdown-content">
              {onNewDocument && (
                <MenuItem
                  icon={<BsFiles />}
                  label={t("tiptapEditor.fileMenu.newDocument")}
                  shortcut="Ctrl+N"
                  onClick={handleNewDocument}
                  disabled={readOnly}
                  dividerAfter
                />
              )}
              {onImportDocx && (
                <MenuItem
                  icon={<FiUpload />}
                  label={t("tiptapEditor.fileMenu.importFromWord")}
                  onClick={handleImportDocxClick}
                  disabled={readOnly}
                />
              )}
              {onImportPdf && (
                <MenuItem
                  icon={<FiUpload />}
                  label={t("tiptapEditor.fileMenu.importPdf")}
                  onClick={handleImportPdfClick}
                  disabled={readOnly}
                />
              )}
              {onExportDocx && (
                <MenuItem
                  icon={<FiDownload />}
                  label={t("tiptapEditor.fileMenu.exportToWord")}
                  onClick={handleExport}
                />
              )}
              {onExportPdf && (
                <MenuItem
                  icon={<BsFilePdf />}
                  label={t("tiptapEditor.fileMenu.exportToPdf")}
                  onClick={handleExportPdf}
                  dividerAfter={!!(onSave || onPrint)}
                />
              )}
              {onPrint && (
                <MenuItem
                  icon={<BsPrinter />}
                  label={t("tiptapEditor.fileMenu.print")}
                  shortcut="Ctrl+P"
                  onClick={handlePrint}
                  dividerAfter={!onPageSetup && !!onSave}
                />
              )}
              {onPageSetup && (
                <MenuItem
                  icon={<BsAspectRatio />}
                  label={t("tiptapEditor.fileMenu.pageSetup", "Page setup…")}
                  onClick={() => {
                    onPageSetup();
                    closeMenu();
                  }}
                  disabled={readOnly}
                  dividerAfter={!!onSave}
                />
              )}
              {onSave && (
                <MenuItem
                  icon={<FiSave />}
                  label={
                    isSaving
                      ? t("tiptapEditor.fileMenu.saving")
                      : t("tiptapEditor.fileMenu.save")
                  }
                  shortcut="Ctrl+S"
                  onClick={handleSave}
                  disabled={isSaving || readOnly}
                />
              )}
              {onVersionHistory && (
                <MenuItem
                  icon={<BsClockHistory />}
                  label={t("tiptapEditor.fileMenu.versionHistory")}
                  onClick={handleVersionHistory}
                />
              )}
            </div>
          )}
          {/* Hidden file inputs */}
          <input
            ref={docxInputRef}
            type="file"
            accept=".docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
            onChange={handleImportDocx}
            style={{ display: "none" }}
          />
          <input
            ref={pdfInputRef}
            type="file"
            accept=".pdf,application/pdf"
            onChange={handleImportPdf}
            style={{ display: "none" }}
          />
        </div>
      )}

      {/* Edit Menu */}
      <div className="menu-dropdown">
        <button
          type="button"
          className={`menu-trigger ${activeMenu === "edit" ? "active" : ""}`}
          onClick={() => toggleMenu("edit")}
        >
          {t("tiptapEditor.menu.edit")}
        </button>
        {activeMenu === "edit" && (
          <div className="menu-dropdown-content">
            <MenuItem
              icon={<BsArrowCounterclockwise />}
              label={t("tiptapEditor.editMenu.undo")}
              shortcut="Ctrl+Z"
              onClick={handleUndo}
              disabled={!editor?.can().undo() || readOnly}
            />
            <MenuItem
              icon={<BsArrowClockwise />}
              label={t("tiptapEditor.editMenu.redo")}
              shortcut="Ctrl+Y"
              onClick={handleRedo}
              disabled={!editor?.can().redo() || readOnly}
              dividerAfter
            />
            <MenuItem
              icon={<BsScissors />}
              label={t("tiptapEditor.editMenu.cut")}
              shortcut="Ctrl+X"
              onClick={handleCut}
              disabled={readOnly}
            />
            <MenuItem
              icon={<BsFiles />}
              label={t("tiptapEditor.editMenu.copy")}
              shortcut="Ctrl+C"
              onClick={handleCopy}
            />
            <MenuItem
              icon={<BsClipboard />}
              label={t("tiptapEditor.editMenu.paste")}
              shortcut="Ctrl+V"
              onClick={handlePaste}
              disabled={readOnly}
            />
            <MenuItem
              icon={<BsTextParagraph />}
              label={t("tiptapEditor.editMenu.pasteWithoutFormatting")}
              shortcut="Ctrl+Shift+V"
              onClick={handlePasteWithoutFormatting}
              disabled={readOnly}
              dividerAfter
            />
            <MenuItem
              icon={<BsCardChecklist />}
              label={t("tiptapEditor.editMenu.selectAll")}
              shortcut="Ctrl+A"
              onClick={handleSelectAll}
            />
            {onFindReplace && (
              <>
                <div className="menu-divider" />
                <MenuItem
                  icon={<BsSearch />}
                  label={t("tiptapEditor.editMenu.findAndReplace")}
                  shortcut="Ctrl+F"
                  onClick={handleFindReplace}
                />
              </>
            )}
          </div>
        )}
      </div>
      {/* View Menu */}
      <div className="menu-dropdown">
        <button
          type="button"
          className={`menu-trigger ${activeMenu === "view" ? "active" : ""}`}
          onClick={() => toggleMenu("view")}
        >
          {t("tiptapEditor.menu.view")}
        </button>
        {activeMenu === "view" && (
          <div className="menu-dropdown-content">
            {/* Document Outline */}
            {onToggleOutlinePanel && (
              <>
                <MenuItem
                  icon={<BsListNested />}
                  label={t("tiptapEditor.viewMenu.documentOutline")}
                  onClick={handleToggleOutlinePanel}
                  checked={showOutlinePanel}
                />
                <div className="menu-divider" />
              </>
            )}
            {/* Track Changes Submenu */}
            <div className="menu-submenu-header">
              {t("tiptapEditor.viewMenu.trackChanges")}
            </div>
            {onToggleTrackChanges && (
              <MenuItem
                icon={trackChangesEnabled ? <BsToggleOn /> : <BsToggleOff />}
                label={
                  trackChangesEnabled
                    ? t("tiptapEditor.viewMenu.trackingEnabled")
                    : t("tiptapEditor.viewMenu.trackingDisabled")
                }
                onClick={handleToggleTrackChanges}
                checked={trackChangesEnabled}
                disabled={readOnly}
              />
            )}
            {trackChangesCount > 0 && (
              <MenuItem
                icon={<BsFileDiff />}
                label={t(
                  "tiptapEditor.viewMenu.showChangesPanel",
                  "Track Changes"
                )}
                onClick={() => closeMenu()}
                badge={trackChangesCount}
                dividerAfter
              />
            )}
            {/* Comments count indicator */}
            {commentsCount > 0 && (
              <MenuItem
                icon={<BsChatDots />}
                label={t("tiptapEditor.viewMenu.showCommentsPanel", "Comments")}
                onClick={() => closeMenu()}
                badge={commentsCount}
              />
            )}
          </div>
        )}
      </div>
      {/* Insert Menu */}
      <div className="menu-dropdown">
        <button
          type="button"
          className={`menu-trigger ${activeMenu === "insert" ? "active" : ""}`}
          onClick={() => toggleMenu("insert")}
        >
          {t("tiptapEditor.menu.insert")}
        </button>
        {activeMenu === "insert" && (
          <div className="menu-dropdown-content">
            {onInsertLink && (
              <MenuItem
                icon={<BsLink45Deg />}
                label={t("tiptapEditor.insertMenu.link")}
                shortcut="Ctrl+K"
                onClick={handleInsertLink}
                disabled={readOnly}
              />
            )}
            <MenuItem
              icon={<BsImage />}
              label={t("tiptapEditor.insertMenu.image")}
              onClick={handleInsertImageClick}
              disabled={readOnly}
            />
            {/* Table with size picker submenu */}
            <div className="menu-item-with-submenu">
              <button
                type="button"
                className={`menu-item ${readOnly ? "disabled" : ""}`}
                onClick={() => setShowTablePicker(!showTablePicker)}
                disabled={readOnly}
              >
                <span className="menu-item-check" />
                <span className="menu-item-icon">
                  <BsTable />
                </span>
                <span className="menu-item-label">
                  {t("tiptapEditor.insertMenu.table")}
                </span>
                <span className="menu-item-arrow">▶</span>
              </button>
              {showTablePicker && (
                <div className="menu-submenu">
                  <div className="table-size-picker">
                    {!showCustomTable ? (
                      <>
                        <div className="table-size-label">
                          {t("tiptapEditor.insertMenu.selectTableSize")}
                        </div>
                        <div className="table-size-grid">
                          {[2, 3, 4, 5].map(rows =>
                            [2, 3, 4, 5].map(cols => (
                              <button
                                key={`${rows}x${cols}`}
                                className="table-size-btn"
                                onClick={() => handleInsertTable(rows, cols)}
                                title={`${rows} × ${cols}`}
                              >
                                {rows}×{cols}
                              </button>
                            ))
                          )}
                        </div>
                        <div className="table-size-divider" />
                        <button
                          className="table-custom-btn"
                          onClick={() => setShowCustomTable(true)}
                        >
                          {t("tiptapEditor.insertMenu.customSize")}
                        </button>
                      </>
                    ) : (
                      <div className="table-custom-form">
                        <div className="table-size-label">
                          {t("tiptapEditor.insertMenu.customTableSize")}
                        </div>
                        <div className="table-custom-inputs">
                          <div className="table-custom-field">
                            <label>{t("tiptapEditor.insertMenu.rows")}</label>
                            <input
                              type="number"
                              min="1"
                              max="50"
                              value={customRows}
                              onChange={e =>
                                setCustomRows(
                                  Math.max(1, parseInt(e.target.value) || 1)
                                )
                              }
                            />
                          </div>
                          <span className="table-custom-x">×</span>
                          <div className="table-custom-field">
                            <label>
                              {t("tiptapEditor.insertMenu.columns")}
                            </label>
                            <input
                              type="number"
                              min="1"
                              max="20"
                              value={customCols}
                              onChange={e =>
                                setCustomCols(
                                  Math.max(1, parseInt(e.target.value) || 1)
                                )
                              }
                            />
                          </div>
                        </div>
                        <div className="table-custom-actions">
                          <button
                            className="table-custom-cancel"
                            onClick={() => setShowCustomTable(false)}
                          >
                            {t("tiptapEditor.insertMenu.back")}
                          </button>
                          <button
                            className="table-custom-submit"
                            onClick={handleCustomTableSubmit}
                          >
                            {t("tiptapEditor.insertMenu.insert")}
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
            <div className="menu-divider" />
            <MenuItem
              icon={<VscHorizontalRule />}
              label={t("tiptapEditor.insertMenu.horizontalLine")}
              onClick={handleInsertHorizontalRule}
              disabled={readOnly}
            />
            <MenuItem
              icon={<MdHorizontalRule />}
              label={t("tiptapEditor.insertMenu.pageBreak")}
              onClick={handleInsertPageBreak}
              disabled={readOnly}
            />
            {onInsertMath && (
              <MenuItem
                icon={<TbMathFunction />}
                label={t("tiptapEditor.insertMenu.mathFormula")}
                onClick={handleInsertMath}
                disabled={readOnly}
              />
            )}
            {onInsertSpecialChar && (
              <MenuItem
                icon={<span style={{ fontWeight: 600 }}>Ω</span>}
                label={t("tiptapEditor.insertMenu.specialChar", {
                  defaultValue: "Special character",
                })}
                onClick={handleInsertSpecialChar}
                disabled={readOnly}
              />
            )}
            <MenuItem
              icon={<BsJournalText />}
              label={t("tiptapEditor.insertMenu.footnote", "Footnote")}
              shortcut={`${MOD_LABEL}+Alt+F`}
              onClick={() => {
                editor?.chain().focus().insertFootnote("footnote").run();
                closeMenu();
              }}
              disabled={readOnly}
            />
            <MenuItem
              icon={<BsJournals />}
              label={t("tiptapEditor.insertMenu.endnote", "Endnote")}
              onClick={() => {
                editor?.chain().focus().insertFootnote("endnote").run();
                closeMenu();
              }}
              disabled={readOnly}
            />
            <MenuItem
              icon={<BsListNested />}
              label={t("tiptapEditor.insertMenu.tableOfContents", {
                defaultValue: "Table of contents",
              })}
              onClick={() => {
                editor?.chain().focus().insertTableOfContents().run();
                closeMenu();
              }}
              disabled={readOnly}
            />

            {onAddComment && (
              <MenuItem
                icon={<BsChatDots />}
                label={t("tiptapEditor.insertMenu.comment")}
                onClick={handleInsertComment}
                disabled={readOnly}
              />
            )}
            {onEditHeaderFooter && (
              <>
                <div className="menu-divider" />
                <MenuItem
                  icon={<BsLayoutTextWindowReverse />}
                  label={t(
                    "tiptapEditor.insertMenu.headerFooter",
                    "Header & Footer"
                  )}
                  onClick={() => {
                    onEditHeaderFooter();
                    closeMenu();
                  }}
                  disabled={readOnly}
                />
              </>
            )}
          </div>
        )}
        {/* Hidden file input for image upload */}
        <input
          ref={imageInputRef}
          type="file"
          accept="image/*"
          onChange={handleImageUpload}
          style={{ display: "none" }}
        />
      </div>
      {/* Format Menu */}
      <div className="menu-dropdown">
        <button
          type="button"
          className={`menu-trigger ${activeMenu === "format" ? "active" : ""}`}
          onClick={() => toggleMenu("format")}
        >
          {t("tiptapEditor.menu.format")}
        </button>
        {activeMenu === "format" && (
          <div className="menu-dropdown-content menu-dropdown-content-format">
            {/* Text Formatting */}
            <div className="menu-submenu-header">
              {t("tiptapEditor.formatMenu.text")}
            </div>
            <MenuItem
              icon={<BsTypeBold />}
              label={t("tiptapEditor.formatMenu.bold")}
              shortcut="Ctrl+B"
              onClick={handleBold}
              checked={editor?.isActive("bold")}
              disabled={readOnly}
            />
            <MenuItem
              icon={<BsTypeItalic />}
              label={t("tiptapEditor.formatMenu.italic")}
              shortcut="Ctrl+I"
              onClick={handleItalic}
              checked={editor?.isActive("italic")}
              disabled={readOnly}
            />
            <MenuItem
              icon={<BsTypeUnderline />}
              label={t("tiptapEditor.formatMenu.underline")}
              shortcut="Ctrl+U"
              onClick={handleUnderline}
              checked={editor?.isActive("underline")}
              disabled={readOnly}
            />
            <MenuItem
              icon={<BsTypeStrikethrough />}
              label={t("tiptapEditor.formatMenu.strikethrough")}
              shortcut="Ctrl+Shift+X"
              onClick={handleStrikethrough}
              checked={editor?.isActive("strike")}
              disabled={readOnly}
            />
            <MenuItem
              icon={<BsSuperscript />}
              label={t("tiptapEditor.formatMenu.superscript")}
              onClick={handleSuperscript}
              checked={editor?.isActive("superscript")}
              disabled={readOnly}
            />
            <MenuItem
              icon={<BsSubscript />}
              label={t("tiptapEditor.formatMenu.subscript")}
              onClick={handleSubscript}
              checked={editor?.isActive("subscript")}
              disabled={readOnly}
              dividerAfter
            />

            {/* Headings */}
            <div className="menu-submenu-header">
              {t("tiptapEditor.formatMenu.headings")}
            </div>
            <MenuItem
              icon={<LuHeading1 />}
              label={t("tiptapEditor.formatMenu.heading1")}
              onClick={handleHeading1}
              checked={editor?.isActive("heading", { level: 1 })}
              disabled={readOnly}
            />
            <MenuItem
              icon={<LuHeading2 />}
              label={t("tiptapEditor.formatMenu.heading2")}
              onClick={handleHeading2}
              checked={editor?.isActive("heading", { level: 2 })}
              disabled={readOnly}
            />
            <MenuItem
              icon={<LuHeading3 />}
              label={t("tiptapEditor.formatMenu.heading3")}
              onClick={handleHeading3}
              checked={editor?.isActive("heading", { level: 3 })}
              disabled={readOnly}
              dividerAfter
            />

            {/* Alignment */}
            <div className="menu-submenu-header">
              {t("tiptapEditor.formatMenu.alignment")}
            </div>
            <MenuItem
              icon={<BsTextLeft />}
              label={t("tiptapEditor.formatMenu.alignLeft")}
              onClick={handleAlignLeft}
              checked={editor?.isActive({ textAlign: "left" })}
              disabled={readOnly}
            />
            <MenuItem
              icon={<BsTextCenter />}
              label={t("tiptapEditor.formatMenu.alignCenter")}
              onClick={handleAlignCenter}
              checked={editor?.isActive({ textAlign: "center" })}
              disabled={readOnly}
            />
            <MenuItem
              icon={<BsTextRight />}
              label={t("tiptapEditor.formatMenu.alignRight")}
              onClick={handleAlignRight}
              checked={editor?.isActive({ textAlign: "right" })}
              disabled={readOnly}
            />
            <MenuItem
              icon={<BsJustify />}
              label={t("tiptapEditor.formatMenu.justify")}
              onClick={handleAlignJustify}
              checked={editor?.isActive({ textAlign: "justify" })}
              disabled={readOnly}
              dividerAfter
            />

            {/* Lists */}
            <div className="menu-submenu-header">
              {t("tiptapEditor.formatMenu.lists")}
            </div>
            <MenuItem
              icon={<BsListUl />}
              label={t("tiptapEditor.formatMenu.bulletList")}
              onClick={handleBulletList}
              checked={editor?.isActive("bulletList")}
              disabled={readOnly}
            />
            <MenuItem
              icon={<BsListOl />}
              label={t("tiptapEditor.formatMenu.numberedList")}
              onClick={handleOrderedList}
              checked={editor?.isActive("orderedList")}
              disabled={readOnly}
              dividerAfter
            />

            {/* Indentation */}
            <div className="menu-submenu-header">
              {t("tiptapEditor.formatMenu.indentation")}
            </div>
            <MenuItem
              icon={<BsTextIndentLeft />}
              label={t("tiptapEditor.formatMenu.increaseIndent")}
              shortcut="Tab"
              onClick={handleIndent}
              disabled={readOnly}
            />
            <MenuItem
              icon={<BsTextIndentRight />}
              label={t("tiptapEditor.formatMenu.decreaseIndent")}
              shortcut="Shift+Tab"
              onClick={handleOutdent}
              disabled={readOnly}
              dividerAfter
            />

            {/* Line Spacing */}
            <div className="menu-submenu-header">
              {t("tiptapEditor.formatMenu.lineSpacing")}
            </div>
            {LINE_HEIGHT_OPTIONS.map(opt => (
              <MenuItem
                key={opt.value}
                icon={<BsDistributeVertical />}
                label={opt.label}
                onClick={() => handleSetLineHeight(opt.value)}
                checked={currentLineHeight === opt.value}
                disabled={readOnly}
              />
            ))}

            {/* Columns (multi-column layout) */}
            <div className="menu-submenu-header">
              {t("tiptapEditor.formatMenu.columns", {
                defaultValue: "Columns",
              })}
            </div>
            <MenuItem
              icon={<BsSquare />}
              label={t("tiptapEditor.formatMenu.oneColumn", {
                defaultValue: "One column",
              })}
              onClick={() => {
                editor?.chain().focus().unsetColumns().run();
                closeMenu();
              }}
              checked={!editor?.isActive("columns")}
              disabled={readOnly}
            />
            <MenuItem
              icon={<BsLayoutThreeColumns />}
              label={t("tiptapEditor.formatMenu.twoColumns", {
                defaultValue: "Two columns",
              })}
              onClick={() => {
                editor?.chain().focus().setColumns(2).run();
                closeMenu();
              }}
              checked={
                editor?.isActive("columns") &&
                editor?.getAttributes("columns").cols === 2
              }
              disabled={readOnly}
            />
            <MenuItem
              icon={<BsLayoutThreeColumns />}
              label={t("tiptapEditor.formatMenu.threeColumns", {
                defaultValue: "Three columns",
              })}
              onClick={() => {
                editor?.chain().focus().setColumns(3).run();
                closeMenu();
              }}
              checked={
                editor?.isActive("columns") &&
                editor?.getAttributes("columns").cols === 3
              }
              disabled={readOnly}
            />

            <div className="menu-divider" />

            {/* Clear Formatting */}
            <MenuItem
              icon={<BsEraser />}
              label={t("tiptapEditor.formatMenu.clearFormatting")}
              onClick={handleClearFormatting}
              disabled={readOnly}
            />

            {onWatermark && (
              <MenuItem
                icon={<span style={{ fontWeight: 700, opacity: 0.5 }}>◈</span>}
                label={t("tiptapEditor.formatMenu.watermark", {
                  defaultValue: "Watermark…",
                })}
                onClick={() => {
                  onWatermark();
                  closeMenu();
                }}
                disabled={readOnly}
              />
            )}
          </div>
        )}
      </div>
      {/* Tools Menu */}
      <div className="menu-dropdown">
        <button
          type="button"
          className={`menu-trigger ${activeMenu === "tools" ? "active" : ""}`}
          onClick={() => toggleMenu("tools")}
        >
          {t("tiptapEditor.menu.tools")}
        </button>
        {activeMenu === "tools" && (
          <div className="menu-dropdown-content">
            {onShowTextStats && (
              <MenuItem
                icon={<BiStats />}
                label={t("tiptapEditor.toolsMenu.wordCount")}
                onClick={handleShowTextStats}
              />
            )}
            {!onShowTextStats && (
              <div className="menu-empty-message">
                {t("tiptapEditor.toolsMenu.noToolsAvailable")}
              </div>
            )}
          </div>
        )}
      </div>
      {/* Help Menu */}
      <div className="menu-dropdown">
        <button
          type="button"
          className={`menu-trigger ${activeMenu === "help" ? "active" : ""} ${!onShowKeyboardShortcuts ? "disabled" : ""}`}
          onClick={() => onShowKeyboardShortcuts && toggleMenu("help")}
          disabled={!onShowKeyboardShortcuts}
        >
          {t("tiptapEditor.menu.help")}
        </button>
        {activeMenu === "help" && onShowKeyboardShortcuts && (
          <div className="menu-dropdown-content">
            <MenuItem
              icon={<BsKeyboard />}
              label={t("tiptapEditor.helpMenu.keyboardShortcuts")}
              onClick={handleShowKeyboardShortcuts}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default EditorMenuBar;
